import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { buildLocalizedPath } from './LocaleRouter';
import { useLanguage } from '../context/LanguageContext';
import { TREATMENT_SERVICES, PATIENT_SUPPORT, FOR_CLINICS, URGENT_CARE_SERVICES, URGENT_CARE_LOCATIONS } from '../data/navigation';

/* Desktop dropdown shell — click to toggle, hover to open, closes on outside click / route change */
const DesktopMenu = ({ label, testid, active, width = 'w-64', children }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname, location.hash]);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        onClick={() => setOpen(true)}
        onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
        aria-haspopup="true"
        aria-expanded={open}
        className={`inline-flex items-center gap-1 px-2 py-1.5 text-[13px] font-semibold rounded-full transition-all duration-200 ${
          active || open ? 'text-[#1FA7A1] bg-[#1FA7A1]/[0.08]' : 'text-slate-700 hover:text-[#1FA7A1] hover:bg-[#1FA7A1]/[0.06]'
        }`}
        data-testid={testid}
      >
        {label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          className={`absolute left-0 top-full pt-2 z-50 whitespace-normal ${width}`}
          data-testid={`${testid}-panel`}
        >
          <div
            className="bg-white rounded-2xl border border-slate-100 p-2"
            style={{ boxShadow: '0 24px 60px rgba(15,42,85,0.14)' }}
          >
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

const MenuLink = ({ to, label, testid, sub }) => {
  const { language } = useLanguage();
  return (
    <Link
      to={buildLocalizedPath(to, language)}
      className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl text-[13px] font-semibold text-slate-700 hover:text-[#1FA7A1] hover:bg-[#F0FAF8] transition-colors"
      data-testid={testid}
    >
      <span>{label}{sub && <span className="block text-[11px] font-medium text-slate-500">{sub}</span>}</span>
      <ArrowRight className="h-3.5 w-3.5 text-slate-300" />
    </Link>
  );
};

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

export const TreatmentsMegaMenu = ({ active }) => {
  const { language } = useLanguage();
  return (
    <DesktopMenu label="Treatments & Services" testid="nav-treatments-services" active={active} width="w-[820px]">
      <div className="grid grid-cols-4 gap-1 p-2">
        {TREATMENT_SERVICES.map(({ label, to, desc, icon: Icon, children }) => (
          <div key={to} className="min-w-0">
            <Link
              to={buildLocalizedPath(to, language)}
              className="group flex items-start gap-2.5 rounded-xl px-3 py-2.5 hover:bg-[#F0FAF8] transition-colors"
              data-testid={`mega-${slug(label)}`}
            >
              <span className="mt-0.5 h-8 w-8 flex-shrink-0 rounded-lg bg-[#F0FAF8] group-hover:bg-white text-[#1FA7A1] inline-flex items-center justify-center transition-colors">
                <Icon className="h-4 w-4" />
              </span>
              <span className="min-w-0">
                <span className="block text-[13px] font-extrabold text-slate-900 group-hover:text-[#1FA7A1] leading-tight">{label}</span>
                <span className="block text-[11px] text-slate-500 leading-snug mt-0.5">{desc}</span>
              </span>
            </Link>
            {children && (
              <ul className="ml-[52px] mt-0.5 mb-1 space-y-0.5">
                {children.map((c) => (
                  <li key={c.to}>
                    <Link
                      to={buildLocalizedPath(c.to, language)}
                      className="block text-[12px] font-semibold text-slate-600 hover:text-[#1FA7A1] py-0.5"
                      data-testid={`mega-checkup-${slug(c.label)}`}
                    >
                      {c.label}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
      <div className="border-t border-slate-100 mt-1 px-4 py-2.5 flex items-center justify-between">
        <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">All treatments &amp; partner clinics</span>
        <Link
          to={buildLocalizedPath('/treatments', language)}
          className="inline-flex items-center gap-1 text-[12.5px] font-bold text-[#1FA7A1] hover:gap-1.5 transition-all"
          data-testid="mega-view-all-treatments"
        >
          View all treatments <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </DesktopMenu>
  );
};

export const PatientSupportMenu = ({ active }) => (
  <DesktopMenu label="Patient Support" testid="nav-patient-support" active={active} width="w-72">
    {PATIENT_SUPPORT.map((i) => <MenuLink key={i.to} {...i} testid={`nav-support-${slug(i.label)}`} />)}
    <div className="border-t border-slate-100 mt-1 pt-1">
      <MenuLink to="/patient-support-vietnam" label="All patient support" testid="nav-support-all" />
    </div>
  </DesktopMenu>
);

export const ForClinicsMenu = ({ active }) => (
  <DesktopMenu label="For Clinics" testid="nav-for-clinics" active={active} width="w-64">
    {FOR_CLINICS.map((i) => <MenuLink key={i.to} {...i} testid={`nav-clinics-${slug(i.label)}`} />)}
  </DesktopMenu>
);

/*
 * Urgent Care — the top-level trigger stays a real LINK to the existing hub
 * (/urgent-medical-care). A disclosure panel opens on hover and on keyboard
 * focus (CSS group-focus-within), so keyboard users can Tab onto the link to
 * reveal it and Tab into the items. No new pages or URLs.
 */
export const UrgentCareMenu = ({ active }) => {
  const { language } = useLanguage();
  return (
    <div className="relative group">
      <Link
        to={buildLocalizedPath('/urgent-medical-care', language)}
        aria-haspopup="true"
        className={`inline-flex items-center gap-1 px-2 py-1.5 text-[13px] font-semibold rounded-full transition-all duration-200 ${
          active
            ? 'text-[#1FA7A1] bg-[#1FA7A1]/[0.08]'
            : 'text-slate-700 hover:text-[#1FA7A1] hover:bg-[#1FA7A1]/[0.06] group-focus-within:text-[#1FA7A1]'
        }`}
        data-testid="nav-urgent-care"
      >
        Urgent Care
        <ChevronDown className="h-3.5 w-3.5 transition-transform group-hover:rotate-180 group-focus-within:rotate-180" />
      </Link>
      <div
        className="absolute left-0 top-full pt-2 z-50 w-72 hidden group-hover:block group-focus-within:block"
        data-testid="nav-urgent-care-panel"
      >
        <div
          className="bg-white rounded-2xl border border-slate-100 p-2"
          style={{ boxShadow: '0 24px 60px rgba(15,42,85,0.14)' }}
        >
          <MenuLink
            to="/urgent-medical-care"
            label="Urgent Care overview"
            sub="24/7 coordination support · availability confirmed per request"
            testid="nav-urgent-overview"
          />
          {URGENT_CARE_SERVICES.map((i) => (
            <MenuLink key={i.to} {...i} testid={`nav-urgent-${slug(i.label)}`} />
          ))}
          <div className="border-t border-slate-100 mt-1 pt-1">
            <p className="px-3 pt-1 pb-1 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
              Locations
            </p>
            {URGENT_CARE_LOCATIONS.map((i) => (
              <MenuLink key={i.to} {...i} testid={`nav-urgent-loc-${slug(i.label)}`} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/* Mobile accordion group */
export const MobileMenuGroup = ({ label, icon, items, testid, onNavigate, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const { language } = useLanguage();
  return (
    <div data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-center gap-3 px-3 py-3 text-slate-800 hover:bg-slate-50 rounded-lg transition-colors font-semibold"
        data-testid={`${testid}-toggle`}
      >
        <span className="text-[#1FA7A1]">{icon}</span>
        <span className="flex-1 text-left">{label}</span>
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <ul className="ml-8 mb-2 border-l border-slate-100 pl-3 space-y-0.5">
          {items.map((i) => (
            <li key={i.to}>
              <Link
                to={buildLocalizedPath(i.to, language)}
                onClick={onNavigate}
                className="block px-2 py-2 text-[14px] font-semibold text-slate-700 hover:text-[#1FA7A1] rounded-md"
                data-testid={`${testid}-${slug(i.label)}`}
              >
                {i.label}
              </Link>
              {i.children && (
                <ul className="ml-3 mb-1 space-y-0.5">
                  {i.children.map((c) => (
                    <li key={c.to}>
                      <Link
                        to={buildLocalizedPath(c.to, language)}
                        onClick={onNavigate}
                        className="block px-2 py-1.5 text-[13px] font-medium text-slate-500 hover:text-[#1FA7A1]"
                        data-testid={`${testid}-checkup-${slug(c.label)}`}
                      >
                        {c.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
